import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/** A service's price in one currency. */
export const servicePriceSchema = z.object({ currency: z.string(), unitPrice: z.number() })

export type ServicePrice = z.infer<typeof servicePriceSchema>

/** A service in the catalog as the operator sees it: active or not, with its tax rate. */
export const catalogItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  categoryId: z.string().nullable(),
  category: z.string().nullable(),
  /** One for each currency the service is for sale in, ordered by currency. */
  prices: z.array(servicePriceSchema),
  taxRate: z.string(),
  unit: z.string(),
  isActive: z.boolean(),
})

export type CatalogItem = z.infer<typeof catalogItemSchema>

/** Amounts and rates go over the wire as decimal strings, never as floats. */
export type CatalogItemInput = {
  code: string
  name: string
  description: string
  categoryId: string | null
  prices: { currency: string; unitPrice: string }[]
  taxRate: string
  unit: string
  isActive: boolean
}

const base = '/v1/exhibitor/admin/service-items'

export function fetchCatalog(signal?: AbortSignal): Promise<{ items: CatalogItem[] }> {
  return api.get(base, {
    schema: z.object({ items: z.array(catalogItemSchema) }),
    ...(signal ? { signal } : {}),
  })
}

export function createCatalogItem(input: CatalogItemInput): Promise<CatalogItem> {
  return api.post(base, { schema: catalogItemSchema, body: input })
}

export function updateCatalogItem(id: string, input: CatalogItemInput): Promise<CatalogItem> {
  return api.patch(`${base}/${id}`, { schema: catalogItemSchema, body: input })
}

export function deleteCatalogItem(id: string) {
  return api.delete(`${base}/${id}`, { schema: z.object({ deleted: z.boolean() }) })
}
